import { datadogRum } from "@datadog/browser-rum";
import { useNavigateById } from "@utils/utils";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiReportReportStatusChoices } from "@api/graphql/types-and-hooks";
import { Legend } from "@components/Legend/Legend";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useReviewGate } from "@components/QaReview/hooks/useReviewGate";
import { ReportSection } from "@components/ReportField/ReportSection";
import { ReportHeaderSection } from "@components/ReportHeaderSection/ReportHeaderSection";
import {
  computeTotalDaysToComplete,
  countInsuranceFlagsRemaining,
  useReportAnalytics,
} from "@hooks/useReportAnalytics";
import { useSignatureHandler } from "@hooks/useSignatureHandler";
import { GeneralInfoSection } from "@pages/EvaluationDetails/Components/GeneralInfoSection";
import {
  FilterSectionSubRoute,
  findErrorSection,
  isGeneratingSection,
  useGetFilteredPageSectionsByLocation,
} from "@pages/EvaluationDetails/Components/helper";
import { FooterButtonRow } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/FooterButtonRow";
import { StickyNav } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/StickyNav/StickyNav";
import { useReport } from "@providers/ReportProvider";

const pageLocation: FilterSectionSubRoute = "review-and-sign";
const topPageElementId: string = "reviewAndSign";

export const EvaluationReviewAndSign: React.FC = () => {
  const filteredSections = useGetFilteredPageSectionsByLocation(pageLocation);
  const appendixSections = useGetFilteredPageSectionsByLocation("appendix");
  const { report, reportQuery } = useReport();
  const reportIsCompleted = report?.reportStatus === ApiReportReportStatusChoices.Completed;
  const { isLoading } = reportQuery;
  const isSectionGenerating = isGeneratingSection(filteredSections);

  const { evaluationId } = useParams<{ evaluationId: string }>();
  // The review is intentionally off for the step-by-step DB flow today (the QA scanner only runs for
  // single-page DB reports), so insurance_flags_remaining stays undefined on DB report_completed
  // events. Forward-compatible: it starts flowing once the scanner is enabled for this flow.
  const { loadedItems } = useReviewData();
  const { isBlocked: isReviewGateBlocked, tooltipReason: reviewGateReason } = useReviewGate();
  // Full reportContext so report_completed carries the same template fields
  // the wizard-level report_opened + wizard_step_* events already do.
  const { captureReportCompleted } = useReportAnalytics({
    report_id: evaluationId ?? "",
    report_type: "diagnosis_builder",
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  });

  const { isGenerating, setIsGenerating, handleSignReport } = useSignatureHandler({
    report: report,
    isCompleted: reportIsCompleted,
    refetchData: reportQuery?.refetch,
    onReportCompleted: () =>
      captureReportCompleted({
        total_days_to_complete: computeTotalDaysToComplete(report?.createdDate),
        insurance_flags_remaining: countInsuranceFlagsRemaining(loadedItems),
      }),
  });

  const [shouldGenerateReport, setShouldGenerateReport] = useState(false);

  useEffect(() => {
    const generateAndSignReport = async () => {
      if (shouldGenerateReport) {
        await handleSignReport(null);
        setShouldGenerateReport(false);

        // log completion to datadog
        const base64DecodedId = atob(report?.id || "");
        const databaseId = base64DecodedId.split("ReportType:")?.pop();
        datadogRum.addAction("report-completion", {
          reportId: databaseId,
          reportType: report?.reportType,
        });
      }
    };

    generateAndSignReport();
  }, [shouldGenerateReport]);

  const scrollToDivById = useNavigateById();

  return (
    <div className="flex w-full flex-col" id={topPageElementId}>
      <div className="grid grid-cols-4 gap-x-1">
        <div id="EvaluationReport" className={`col-span-3 flex w-full flex-col gap-y-4`}>
          {report?.reportTemplate?.isBaseTemplate ? null : (
            <ReportHeaderSection
              report={report}
              refetchData={reportQuery?.refetch}
              reportIsCompleted={reportIsCompleted}
            />
          )}
          <GeneralInfoSection allowInLineEdit={!reportIsCompleted} />
          {filteredSections.map((section) => (
            <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
          ))}
          <div className=" -mt-4">
            <Legend title="Appendix" position="center" />
          </div>
          {appendixSections.map((section) => (
            <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
          ))}
        </div>

        <div className="flex w-full flex-col pl-8">
          <StickyNav state={filteredSections} reportIsCompleted={reportIsCompleted} />
        </div>
      </div>
      {!reportIsCompleted ? (
        <FooterButtonRow
          dataTestID="complete-report-button"
          primaryButtonTitle="Complete Report"
          primaryButtonLoading={isGenerating || isLoading}
          primaryButtonDisabled={isSectionGenerating || isReviewGateBlocked}
          primaryButtonDisabledTooltipReason={reviewGateReason ?? ""}
          primaryButtonAction={async () => {
            const errorSection = findErrorSection(filteredSections);
            if (errorSection) {
              scrollToDivById(errorSection.id);
            } else {
              setIsGenerating(true);
              setShouldGenerateReport(true);
              scrollToDivById(topPageElementId);
            }
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
